/**
 * Keep-alive for the search results page (SRP), so browser Back from a job page to the search
 * you came from is instant: no pageContext refetch wait, no SRP re-render, no Back morph, and the
 * page comes back exactly as you left it (scroll, expanded cards, loaded results).
 *
 * Client-only. The server keeps rendering just the requested page; nothing here runs during SSR,
 * and nothing here is reached unless the `dev_enable_srp_keep_alive` flag is on.
 *
 * How it works:
 * - The kept SRP lives in its own React root on a holder element appended after #root
 *   (`data-reactroot`, so it gets #root's own sizing rules from index.css). Its tree is wrapped in
 *   React's <Activity> from the first render, so flipping `mode` between 'hidden' and 'visible'
 *   preserves its state and DOM. While hidden, Activity also tears down the SRP's effects; that
 *   matters because SearchProvider listens to popstate/urlchange and would otherwise read a job
 *   URL as "a search with no filters", reset itself, and write search params into the job URL.
 * - Leaving an SRP rendered the normal way (the hydrated island on a fresh load, or the #root tree
 *   after a soft navigation) can't keep that exact tree alive: it was never under an <Activity>,
 *   and wrapping it now would remount it. So instead a hidden copy is pre-rendered from the same
 *   pageContext data at idle priority, and only when the URL still matches the one that data was
 *   built for (client-side filter changes since then would make it show stale results).
 * - Leaving the kept SRP itself just hides it, so from the first Back onwards the real, live page
 *   (including any filter changes made on it) is what comes back.
 * - Back to the kept URL reveals it from +onPageTransitionStart (before Vike's pageContext fetch),
 *   then +onRenderClient sees the kept page owns the route and only clears #root.
 * - Navigating to a different SRP URL disposes the kept one first, so two SRPs never coexist.
 */
import React, { Activity } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { flushSync } from 'react-dom';
import type { PageContextClient } from 'vike/types';
import { matchSearchIndexPath } from '../../pages/index/match-search-index-path';
import { resolveSrpViewModel, type SrpPageDataForViewModel } from '../../features/srp/srp-view-model-wire';
import type { SrpViewModel } from '../../features/srp/SrpViewModel';
import { loadTopEmployersStripDeduped } from '../srp/fetch-top-employers-strip-api';
import { loadTopRolesStripDeduped } from '../srp/fetch-top-roles-strip-api';
import { scheduleIdleCallbackWithFallback } from '../browser/scheduleIdleCallbackWithFallback';
import { getReactRootSentryOptions } from '../react/react-root-sentry-options';
import { isSrpKeepAliveEnabled } from '../feature-flags/srp-keep-alive-flag';
import type { SessionUser } from '../../types/auth';
import type { CookieMatchProfile } from '../shared/client-preferences';

export const SRP_KEEP_ALIVE_HOLDER_ID = 'srp-keepalive';

type Kept = {
  container: HTMLElement;
  root: Root;
  element: React.ReactElement;
  /** pathname + search of the search this page is showing. */
  href: string;
  visible: boolean;
};

let kept: Kept | null = null;
/** pageContext + href of the SRP most recently rendered the normal way (island or #root). */
let lastNormalSrp: { pageContext: PageContextClient; renderedHref: string } | null = null;
/** Latest URL of whichever SRP is currently on screen, kept current through filter changes. */
let currentSrpHref: string | null = null;
let currentPageIsSrp = false;
let listening = false;
/** Bumped on every dispose, so a pre-render scheduled before it can tell it's been superseded. */
let generation = 0;

function hrefOf(loc: Location | URL): string {
  return `${loc.pathname}${loc.search}`;
}

function isSrpPath(pathname: string): boolean {
  return Boolean(matchSearchIndexPath(pathname));
}

function getRootContainer(): HTMLElement | null {
  return document.getElementById('root');
}

function renderKept(k: Kept): void {
  k.root.render(<Activity mode={k.visible ? 'visible' : 'hidden'}>{k.element}</Activity>);
}

function ensureUrlTracking(): void {
  if (listening || typeof window === 'undefined') return;
  listening = true;
  // SearchProvider patches history.pushState/replaceState to emit `urlchange`; filter, sort and
  // page changes on the SRP all go through it. Track the SRP's own URL while it's on screen so a
  // kept page is matched against what the visitor was actually looking at when they left.
  const track = () => {
    if (currentPageIsSrp && isSrpPath(window.location.pathname)) {
      currentSrpHref = hrefOf(window.location);
      if (kept?.visible) kept.href = currentSrpHref;
    }
  };
  window.addEventListener('urlchange', track);
  window.addEventListener('popstate', track);
}

export function isKeptSrpVisible(): boolean {
  return Boolean(kept?.visible);
}

function showKept(k: Kept): void {
  // flushSync so the kept page's DOM is visible in the same frame #root disappears (no blank
  // flash). If its hidden pre-render hasn't finished yet, this finishes it synchronously.
  k.visible = true;
  flushSync(() => renderKept(k));
  k.container.style.display = '';
  const root = getRootContainer();
  if (root) root.style.display = 'none';
}

function hideKept(k: Kept): void {
  k.visible = false;
  k.container.style.display = 'none';
  renderKept(k);
  const root = getRootContainer();
  if (root) root.style.display = '';
}

export function disposeKeptSrp(): void {
  generation += 1;
  if (!kept) return;
  const k = kept;
  kept = null;
  const root = getRootContainer();
  if (root) root.style.display = '';
  k.root.unmount();
  k.container.remove();
}

/**
 * From +onPageTransitionStart: reveal the kept SRP right away when this is a Back to the exact
 * search it shows. Returns true when it did (the caller skips the Back morph).
 */
export function revealKeptSrpForBackNavigation(pageContext: {
  isBackwardNavigation?: boolean | null;
  urlOriginal?: string;
}): boolean {
  if (typeof window === 'undefined' || !kept || kept.visible) return false;
  if (pageContext.isBackwardNavigation !== true || !pageContext.urlOriginal) return false;
  if (!isSrpKeepAliveEnabled()) return false;
  // The destination, not window.location: Vike calls this hook while the URL may still be the
  // page being left (see +onPageTransitionStart.ts).
  let destination: string;
  try {
    destination = hrefOf(new URL(pageContext.urlOriginal, window.location.origin));
  } catch {
    return false;
  }
  if (kept.href !== destination) return false;
  showKept(kept);
  currentPageIsSrp = true;
  currentSrpHref = kept.href;
  return true;
}

async function buildSrpElement(pageContext: PageContextClient): Promise<React.ReactElement | null> {
  const data = (pageContext as { data?: SrpPageDataForViewModel }).data;
  let viewModel: SrpViewModel | null = resolveSrpViewModel(data);
  if (!viewModel) return null;
  // Same strips the island patches in after hydration (srp.client.ts); deduped, so this is
  // normally an already-settled promise rather than a new request.
  const needsEmployers = !viewModel.location && !viewModel.topEmployersStrip?.length;
  const needsRoles = !viewModel.location && !viewModel.topRolesStrip?.length;
  if (needsEmployers || needsRoles) {
    try {
      const [employers, roles] = await Promise.all([
        needsEmployers ? loadTopEmployersStripDeduped() : Promise.resolve(null),
        needsRoles ? loadTopRolesStripDeduped() : Promise.resolve(null),
      ]);
      viewModel = {
        ...viewModel,
        ...(employers && employers.length > 0 ? { topEmployersStrip: employers } : {}),
        ...(roles && roles.length > 0 ? { topRolesStrip: roles } : {}),
      };
    } catch {
      /* strips are optional */
    }
  }
  const { SrpApp } = await import('../../features/srp/SrpApp');
  return (
    <SrpApp
      initialViewModel={viewModel}
      initialUser={((pageContext as { user?: SessionUser | null }).user ?? null)}
      initialHasBackofficeAccess={Boolean((pageContext as { hasBackofficeAccess?: boolean }).hasBackofficeAccess)}
      initialMatchProfileCookieFallback={
        ((data as { matchProfileCookieFallback?: CookieMatchProfile | null } | undefined)?.matchProfileCookieFallback ??
          null) as CookieMatchProfile | null
      }
      isClientSide={true}
      initialExperiments={(pageContext as { experiments?: Record<string, string> }).experiments ?? {}}
    />
  );
}

function prerenderHiddenSrp(pageContext: PageContextClient, href: string): void {
  const myGeneration = generation;
  scheduleIdleCallbackWithFallback(
    () => {
      void buildSrpElement(pageContext).then((element) => {
        // Superseded (another SRP rendered, or Back already happened without it) or nothing to show.
        if (!element || myGeneration !== generation || kept) return;
        if (window.location.pathname && isSrpPath(window.location.pathname)) return;
        const container = document.createElement('div');
        container.id = SRP_KEEP_ALIVE_HOLDER_ID;
        container.setAttribute('data-reactroot', '');
        container.style.display = 'none';
        document.body.appendChild(container);
        const k: Kept = {
          container,
          root: createRoot(container, getReactRootSentryOptions()),
          element,
          href,
          visible: false,
        };
        kept = k;
        // Hidden <Activity> content renders at offscreen priority, so this pre-render yields to
        // the job page instead of competing with it.
        renderKept(k);
      });
    },
    { timeout: 3000 },
  );
}

export type KeepAliveRenderDecision = 'kept-srp-owns-route' | 'render-normally';

/**
 * From +onRenderClient, for every render. `isSrpDestination` is the caller's own route check and
 * `leavingNormalSrp` whether a normally-rendered SRP (#srp-ssr) is still in the DOM. Returns
 * 'kept-srp-owns-route' when the kept page is (now) showing this route and the caller should only
 * clear #root; otherwise the caller renders as usual.
 */
export function onKeepAliveRender(
  pageContext: PageContextClient,
  { isSrpDestination, leavingNormalSrp }: { isSrpDestination: boolean; leavingNormalSrp: boolean },
): KeepAliveRenderDecision {
  if (typeof window === 'undefined') return 'render-normally';
  const enabled = isSrpKeepAliveEnabled();
  if (!enabled) {
    if (kept) disposeKeptSrp();
    currentPageIsSrp = isSrpDestination;
    return 'render-normally';
  }
  ensureUrlTracking();
  const href = hrefOf(window.location);

  if (isSrpDestination) {
    if (kept && kept.href === href) {
      if (!kept.visible) showKept(kept);
      currentPageIsSrp = true;
      currentSrpHref = href;
      return 'kept-srp-owns-route';
    }
    // A different search: never keep two SRPs in the DOM.
    disposeKeptSrp();
    currentPageIsSrp = true;
    currentSrpHref = href;
    lastNormalSrp = { pageContext, renderedHref: href };
    return 'render-normally';
  }

  // Destination is not an SRP.
  const leavingFrom = currentPageIsSrp ? currentSrpHref : null;
  currentPageIsSrp = false;
  if (kept?.visible) {
    // Leaving the live kept page: just hide it, it keeps everything.
    hideKept(kept);
    return 'render-normally';
  }
  if (leavingNormalSrp && leavingFrom && lastNormalSrp && lastNormalSrp.renderedHref === leavingFrom) {
    // Leaving a normally-rendered SRP whose data still matches what was on screen.
    disposeKeptSrp();
    prerenderHiddenSrp(lastNormalSrp.pageContext, leavingFrom);
  }
  return 'render-normally';
}

/** Test-only: reset module state. */
export function __resetSrpKeepAliveForTests(): void {
  disposeKeptSrp();
  lastNormalSrp = null;
  currentSrpHref = null;
  currentPageIsSrp = false;
}
